import React, { useState } from 'react';
import { Box, Typography, IconButton, alpha, Collapse } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { COLORS, FONT_SIZES, FONT_WEIGHTS } from '../../theme/constants';

/**
 * InfoBox - A modern, polished informational alert box
 * Features: Gradient accents, smooth animations, multiple variants
 */
const InfoBox = ({
  title,
  description,
  items = [],
  icon,
  dismissible = false,
  onDismiss,
  variant = 'info', // 'info' | 'warning' | 'success' | 'error' | 'tip'
  compact = false,
  sx = {},
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const variantConfig = {
    info: { 
      bg: '#F0F9FF', 
      color: '#3B82F6',
      gradient: 'linear-gradient(135deg, #3B82F6, #60A5FA)',
      icon: <InfoOutlinedIcon />,
    },
    warning: { 
      bg: '#FFFBEB', 
      color: '#F59E0B',
      gradient: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
      icon: <WarningAmberIcon />,
    },
    success: { 
      bg: '#F0FDF4', 
      color: '#10B981',
      gradient: 'linear-gradient(135deg, #10B981, #34D399)',
      icon: <CheckCircleOutlineIcon />,
    },
    error: { 
      bg: '#FEF2F2', 
      color: '#EF4444',
      gradient: 'linear-gradient(135deg, #EF4444, #F87171)',
      icon: <ErrorOutlineIcon />,
    },
    tip: { 
      bg: '#F5F3FF', 
      color: '#8B5CF6',
      gradient: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
      icon: <LightbulbOutlinedIcon />,
    },
  };

  const config = variantConfig[variant] || variantConfig.info;
  const displayIcon = icon || config.icon;

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) onDismiss();
  };

  return (
    <Collapse in={isVisible}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: compact ? 1.5 : 2,
          p: compact ? 1.5 : 2.5,
          py: compact ? 1 : 2.5,
          backgroundColor: config.bg,
          borderRadius: compact ? 2 : 3,
          position: 'relative',
          overflow: 'hidden',
          border: `1px solid ${alpha(config.color, 0.2)}`,
          transition: 'all 0.3s ease',
          
          // Left gradient accent bar
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: compact ? '3px' : '4px',
            background: config.gradient,
          },
          
          '&:hover': {
            boxShadow: `0 4px 20px ${alpha(config.color, 0.15)}`,
          },
          
          ...sx,
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: compact ? 28 : 40,
            height: compact ? 28 : 40,
            borderRadius: compact ? 1.5 : 2,
            background: config.gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: `0 4px 12px ${alpha(config.color, 0.3)}`,
          }}
        >
          {React.cloneElement(displayIcon, { 
            sx: { 
              fontSize: compact ? 16 : 22, 
              color: '#fff',
            } 
          })}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Title */}
          {title && (
            <Typography
              sx={{
                fontWeight: FONT_WEIGHTS.semiBold,
                fontSize: compact ? FONT_SIZES.caption : FONT_SIZES.body1,
                color: config.color,
                mb: (description || items.length > 0) ? (compact ? 0.25 : 0.75) : 0,
                lineHeight: 1.4,
              }}
            >
              {title}
            </Typography>
          )}

          {/* Description */}
          {description && (
            <Typography
              sx={{
                fontSize: compact ? FONT_SIZES.caption : FONT_SIZES.body2,
                color: COLORS.text.secondary,
                lineHeight: compact ? 1.4 : 1.6,
                mb: items.length > 0 ? (compact ? 1 : 1.5) : 0,
              }}
            >
              {description}
            </Typography>
          )}

          {/* Bullet list */}
          {items.length > 0 && (
            <Box
              component="ul"
              sx={{
                pl: 2.5,
                m: 0,
                '& li': {
                  fontSize: FONT_SIZES.body2,
                  color: COLORS.text.secondary,
                  lineHeight: 1.8,
                  position: 'relative',
                  '&::marker': {
                    color: config.color,
                  },
                },
              }}
            >
              {items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </Box>
          )}
        </Box>

        {/* Dismiss button */}
        {dismissible && (
          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: COLORS.text.secondary,
              opacity: 0.6,
              transition: 'all 0.2s ease',
              '&:hover': {
                opacity: 1,
                backgroundColor: alpha(config.color, 0.1),
                color: config.color,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>
    </Collapse>
  );
};

export default InfoBox;
